import React, { useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { 
  Upload,
  Search,
  Folder,
  Image as ImageIcon,
  Download,
  Edit,
  Trash2,
  Copy,
  Move,
  Plus,
  ChevronRight,
  Grid3X3,
  List,
  RefreshCw,
  FolderOpen,
  FileImage,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import {
  useRepositoryImages,
  useImageFolders,
  useCreateFolder,
  useUploadMultipleImages,
  useDeleteImage,
  useUpdateImage,
  useDeleteFolder,
  useMoveImage,
  buildFolderTree,
  type ImageFolder,
  type RepositoryImage
} from '@/hooks/useRepositoryImages';

export default function Assets() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [page, setPage] = useState(1);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Folder creation state
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [folderDescription, setFolderDescription] = useState('');

  // Image editing state
  const [editingImage, setEditingImage] = useState<RepositoryImage | null>(null);
  const [imageDescription, setImageDescription] = useState('');

  // Data hooks
  const { data: folders, isLoading: foldersLoading } = useImageFolders();
  const { data: imagesData, isLoading: imagesLoading, refetch } = useRepositoryImages({
    searchQuery,
    folderId: currentFolderId,
    page,
    pageSize: 24
  });

  // Mutation hooks
  const createFolderMutation = useCreateFolder();
  const uploadMutation = useUploadMultipleImages();
  const deleteImageMutation = useDeleteImage();
  const updateImageMutation = useUpdateImage();
  const deleteFolderMutation = useDeleteFolder();
  const moveImageMutation = useMoveImage();

  // Build folder tree
  const folderTree = folders ? buildFolderTree(folders) : [];

  // Handle file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    await uploadMutation.mutateAsync({
      files,
      folderId: currentFolderId
    });

    // Clear the input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle folder creation
  const handleCreateFolder = async () => {
    if (!folderName.trim()) return;

    await createFolderMutation.mutateAsync({
      name: folderName,
      description: folderDescription || undefined,
      parent_id: currentFolderId
    });

    setShowCreateFolder(false);
    setFolderName('');
    setFolderDescription('');
  };

  // Handle image deletion
  const handleDeleteImage = async (image: RepositoryImage) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    await deleteImageMutation.mutateAsync(image);
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedImages.size === 0) return;
    if (!window.confirm(`Delete ${selectedImages.size} images?`)) return;

    const images = imagesData?.images.filter(img => selectedImages.has(img.id)) || [];
    for (const image of images) {
      await deleteImageMutation.mutateAsync(image);
    }
    setSelectedImages(new Set());
  };

  // Handle image update
  const handleUpdateImage = async () => {
    if (!editingImage) return;

    await updateImageMutation.mutateAsync({
      id: editingImage.id,
      description: imageDescription
    });

    setEditingImage(null);
    setImageDescription('');
  };

  // Copy image URL
  const copyImageUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('Copied!', 'Image URL copied to clipboard');
  };

  // Folder navigation breadcrumbs
  const getBreadcrumbs = () => {
    const breadcrumbs = [{ id: null, name: 'Root' }];
    if (currentFolderId && folders) {
      const folder = folders.find(f => f.id === currentFolderId);
      if (folder) {
        // Build path from current folder to root
        const path = [];
        let current = folder;
        while (current) {
          path.unshift(current);
          current = folders.find(f => f.id === current.parent_id) || null;
        }
        breadcrumbs.push(...path.map(f => ({ id: f.id, name: f.name })));
      }
    }
    return breadcrumbs;
  };

  // Render folder tree recursively
  const renderFolderTree = (folders: (ImageFolder & { children?: ImageFolder[] })[], level = 0) => {
    return folders.map(folder => (
      <div key={folder.id}>
        <button
          onClick={() => setCurrentFolderId(folder.id)}
          className={cn(
            'w-full flex items-center gap-2 px-3 py-2 hover:bg-accent rounded-md text-sm',
            currentFolderId === folder.id && 'bg-accent'
          )}
          style={{ paddingLeft: `${(level * 20) + 12}px` }}
        >
          {folder.children && folder.children.length > 0 ? (
            <FolderOpen className="h-4 w-4" />
          ) : (
            <Folder className="h-4 w-4" />
          )}
          <span className="flex-1 text-left truncate">{folder.name}</span>
        </button>
        {folder.children && folder.children.length > 0 && (
          <div>
            {renderFolderTree(folder.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Image Repository</h1>
          <p className="text-muted-foreground mt-1">Manage your image assets and organize them into folders</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={refetch} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => fileInputRef.current?.click()}>
            <Upload className="h-4 w-4 mr-2" />
            Upload Images
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={handleFileUpload}
          />
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar - Folder Tree */}
        <div className="col-span-3">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-center">
                <CardTitle className="text-base">Folders</CardTitle>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowCreateFolder(true)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[600px]">
                <div className="p-3 space-y-1">
                  <button
                    onClick={() => setCurrentFolderId(null)}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-2 hover:bg-accent rounded-md text-sm',
                      currentFolderId === null && 'bg-accent'
                    )}
                  >
                    <Folder className="h-4 w-4" />
                    <span className="flex-1 text-left">All Images</span>
                  </button>
                  <Separator className="my-2" />
                  {foldersLoading ? (
                    <div className="text-center py-4 text-muted-foreground">Loading folders...</div>
                  ) : (
                    renderFolderTree(folderTree)
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Main Content - Images */}
        <div className="col-span-9 space-y-4">
          {/* Search and View Controls */}
          <Card>
            <CardContent className="p-4">
              <div className="flex justify-between items-center gap-4">
                <div className="flex-1 max-w-md">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search images..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {selectedImages.size > 0 && (
                    <>
                      <Badge variant="secondary">{selectedImages.size} selected</Badge>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={handleBulkDelete}
                      >
                        Delete Selected
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedImages(new Set())}
                      >
                        Clear Selection
                      </Button>
                    </>
                  )}
                  <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'grid' | 'list')}>
                    <TabsList>
                      <TabsTrigger value="grid">
                        <Grid3X3 className="h-4 w-4" />
                      </TabsTrigger>
                      <TabsTrigger value="list">
                        <List className="h-4 w-4" />
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </div>

              {/* Breadcrumbs */}
              <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
                {getBreadcrumbs().map((crumb, index) => (
                  <React.Fragment key={crumb.id || 'root'}>
                    {index > 0 && <ChevronRight className="h-4 w-4" />}
                    <button
                      onClick={() => setCurrentFolderId(crumb.id)}
                      className="hover:text-foreground transition-colors"
                    >
                      {crumb.name}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Images Grid/List */}
          <Card>
            <CardContent className="p-6">
              {imagesLoading ? (
                <div className="text-center py-12 text-muted-foreground">
                  Loading images...
                </div>
              ) : imagesData?.images.length === 0 ? (
                <div className="text-center py-12">
                  <FileImage className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No images found</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Images
                  </Button>
                </div>
              ) : (
                <>
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-4 gap-4">
                      {imagesData?.images.map((image) => (
                        <div
                          key={image.id}
                          className={cn(
                            "group relative rounded-lg overflow-hidden border cursor-pointer",
                            selectedImages.has(image.id) && "ring-2 ring-primary"
                          )}
                          onClick={() => {
                            const newSelected = new Set(selectedImages);
                            if (newSelected.has(image.id)) {
                              newSelected.delete(image.id);
                            } else {
                              newSelected.add(image.id);
                            }
                            setSelectedImages(newSelected);
                          }}
                        >
                          <img
                            src={image.public_url}
                            alt={image.file_name}
                            className="w-full h-48 object-cover"
                          />
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity p-3 flex flex-col justify-between">
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyImageUrl(image.public_url || '');
                                }}
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingImage(image);
                                  setImageDescription(image.description || '');
                                }}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteImage(image);
                                }}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                            <div>
                              <p className="text-white text-sm font-medium truncate">
                                {image.file_name}
                              </p>
                              <p className="text-white/80 text-xs">
                                {(image.file_size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {imagesData?.images.map((image) => (
                        <div
                          key={image.id}
                          className={cn(
                            "flex items-center gap-4 p-3 rounded-lg hover:bg-accent cursor-pointer",
                            selectedImages.has(image.id) && "bg-accent"
                          )}
                          onClick={() => {
                            const newSelected = new Set(selectedImages);
                            if (newSelected.has(image.id)) {
                              newSelected.delete(image.id);
                            } else {
                              newSelected.add(image.id);
                            }
                            setSelectedImages(newSelected);
                          }}
                        >
                          <img
                            src={image.public_url}
                            alt={image.file_name}
                            className="w-16 h-16 object-cover rounded"
                          />
                          <div className="flex-1">
                            <p className="font-medium">{image.file_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {(image.file_size / 1024 / 1024).toFixed(2)} MB • {image.mime_type}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyImageUrl(image.public_url || '');
                              }}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingImage(image);
                                setImageDescription(image.description || '');
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteImage(image);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Pagination */}
                  {imagesData && imagesData.totalPages > 1 && (
                    <div className="flex justify-center items-center gap-2 mt-6">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {page} of {imagesData.totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => p + 1)}
                        disabled={!imagesData.hasMore}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Create Folder Dialog */}
      <Dialog open={showCreateFolder} onOpenChange={setShowCreateFolder}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
            <DialogDescription>
              Create a new folder to organize your images
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="folder-name">Folder Name</Label>
              <Input
                id="folder-name"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                placeholder="Enter folder name"
              />
            </div>
            <div>
              <Label htmlFor="folder-description">Description (optional)</Label>
              <Textarea
                id="folder-description"
                value={folderDescription}
                onChange={(e) => setFolderDescription(e.target.value)}
                placeholder="Enter folder description"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreateFolder(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateFolder} disabled={!folderName.trim()}>
                Create Folder
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Image Dialog */}
      <Dialog open={!!editingImage} onOpenChange={() => setEditingImage(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Image Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {editingImage && (
              <>
                <img
                  src={editingImage.public_url}
                  alt={editingImage.file_name}
                  className="w-full h-48 object-contain rounded"
                />
                <div>
                  <Label>File Name</Label>
                  <p className="text-sm text-muted-foreground">{editingImage.file_name}</p>
                </div>
                <div>
                  <Label htmlFor="image-description">Description</Label>
                  <Textarea
                    id="image-description"
                    value={imageDescription}
                    onChange={(e) => setImageDescription(e.target.value)}
                    placeholder="Enter image description"
                    rows={3}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setEditingImage(null)}>
                    Cancel
                  </Button>
                  <Button onClick={handleUpdateImage}>
                    Save Changes
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}